import { create } from 'zustand';
import { z } from 'zod';
import type { ChecklistBrand, InsuranceChecklist } from '../types';
import { ChecklistBrandSchema } from '../schemas/firestoreSchemas';
import { siteBaseUrl } from '../utils/share';

const ACTIVE_BRAND_KEY = 'redi:activeBrand:v1';

/** A checklist's brand plus the checklist it came from, so a later resolve
 *  can tell "already showing this brand" from "a different link's brand". */
export type ActiveBrand = ChecklistBrand & { checklistId: string };

const ActiveBrandSchema = ChecklistBrandSchema.extend({ checklistId: z.string() });

// Same sessionStorage-mirroring shape as uiStore's pendingProperty /
// previewEvaluation (see the comment there): synchronous, so the store
// hydrates inline at creation with no null-until-hydrated dance, and it
// survives a reload but not a new browser session. Kept local to this store
// rather than imported from uiStore — this store owns no uiStore state and
// the two helpers are a few lines each.
function readSessionJson<T>(key: string): T | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeSessionJson(key: string, value: unknown): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    if (value == null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // non-fatal — in-memory state still works for this page's lifetime
  }
}

function hydrateFromSession(): ActiveBrand | null {
  const raw = readSessionJson<unknown>(ACTIVE_BRAND_KEY);
  if (raw == null) return null;
  const parsed = ActiveBrandSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/** The brand a checklist carries for the insurer-link journey, or null for a
 *  brandless checklist (or no checklist at all). Pure — no store reads. */
export function brandFromChecklist(checklist: InsuranceChecklist | null): ActiveBrand | null {
  if (!checklist?.brand) return null;
  return { ...checklist.brand, checklistId: checklist.id };
}

/** Resolves a brand's logoUrl for display: a site-relative path (seed data
 *  stores marks under `public/brand/`, e.g. `/brand/summit-ridge-mutual.svg`)
 *  is joined to the current origin; an already-absolute URL passes through
 *  unchanged. */
export function resolveBrandLogoUrl(url: string): string {
  if (url.startsWith('/')) return `${siteBaseUrl()}${url}`;
  return url;
}

interface BrandState {
  // The co-brand strip + accent override currently in effect (see
  // src/theme/brand.ts). Session-scoped and sessionStorage-mirrored on web,
  // same as uiStore's insurer-link fields — and deliberately NOT cleared on
  // sign-out for the same reason: the try-then-save round trip crosses an
  // auth boundary, and a mid-journey sign-out/sign-in must not drop the
  // visitor's insurer branding.
  activeBrand: ActiveBrand | null;
  setActiveBrand: (brand: ActiveBrand | null) => void;
  /** Re-read sessionStorage into state. The store already hydrates inline at
   *  creation (sessionStorage is synchronous); this exists for callers that
   *  need to re-sync after the fact, e.g. a test that swaps sessionStorage
   *  out from under an already-created store. */
  hydrateActiveBrand: () => void;
}

export const useBrandStore = create<BrandState>((set) => ({
  activeBrand: hydrateFromSession(),
  setActiveBrand: (brand) => {
    set({ activeBrand: brand });
    writeSessionJson(ACTIVE_BRAND_KEY, brand);
  },
  hydrateActiveBrand: () => set({ activeBrand: hydrateFromSession() }),
}));
